import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { getApiUrl } from '@/utils/apiUrl';
import { toast } from 'sonner';

const API = getApiUrl();

const notifyIncoming = (call) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('Appel entrant', { body: `${call.caller_name} vous appelle`, icon: '/favicon.ico', tag: `call-${call.id}` });
  }
};

export function useSoftPhone() {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [view, setView] = useState('idle'); // idle | ringing | incoming | active
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOff, setIsSpeakerOff] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [recentCalls, setRecentCalls] = useState([]);
  const [tab, setTab] = useState('contacts');
  const durationRef = useRef(null);
  const pollRef = useRef(null);

  const isStaff = user && !['client'].includes(user.role);

  const fetchContacts = useCallback(async (q = '') => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/communications/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: q ? { search: q } : {},
      });
      setContacts(res.data);
    } catch (e) { void 0; }
  }, [token, user]);

  const fetchRecentCalls = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/communications/calls`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 20 },
      });
      setRecentCalls(res.data.calls || []);
    } catch (e) { void 0; }
  }, [token, user]);

  const pollIncomingCalls = useCallback(async () => {
    if (!user || view === 'active' || view === 'ringing') return;
    try {
      const res = await axios.get(`${API}/communications/calls/active`, {
        headers: { Authorization: `Bearer ${token}` },
        _skipCache: true,
      });
      const incoming = (res.data || []).find(c => c.callee_id === user.id && c.status === 'ringing');
      if (incoming && (!currentCall || currentCall.id !== incoming.id)) {
        setCurrentCall(incoming);
        setView('incoming');
        setIsOpen(true);
        setIsMinimized(false);
        notifyIncoming(incoming);
      }
    } catch (e) { void 0; }
  }, [token, user, view, currentCall]);

  useEffect(() => {
    if (isOpen) { fetchContacts(); fetchRecentCalls(); }
  }, [isOpen, fetchContacts, fetchRecentCalls]);

  useEffect(() => {
    if (search) {
      const timeout = setTimeout(() => fetchContacts(search), 300);
      return () => clearTimeout(timeout);
    } else if (isOpen) { fetchContacts(); }
  }, [search, fetchContacts, isOpen]);

  // Poll for incoming calls every 5 seconds
  useEffect(() => {
    pollRef.current = setInterval(pollIncomingCalls, 5000);
    return () => clearInterval(pollRef.current);
  }, [pollIncomingCalls]);

  // Duration timer
  useEffect(() => {
    if (view === 'active') {
      setDuration(0);
      durationRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } else { clearInterval(durationRef.current); }
    return () => clearInterval(durationRef.current);
  }, [view]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const headers = { Authorization: `Bearer ${token}` };

  // Simulate answer after 3s (with LiveKit this would be event-driven)
  const autoAnswer = (callId) => setTimeout(() => {
    setView('active');
    axios.post(`${API}/communications/call/${callId}/answer`, {}, { headers }).catch(() => {});
  }, 3000);

  const startOutgoing = async (contact, path, payload) => {
    const res = await axios.post(`${API}/communications/${path}`, payload, { headers });
    setCurrentCall(res.data);
    setSelectedContact(contact);
    setView('ringing');
    return res.data;
  };

  const initiateCall = async (contact, type = 'webrtc') => {
    try {
      const call = await startOutgoing(contact, 'call', { callee_id: contact.id, type });
      toast.success(`Appel en cours vers ${contact.full_name}...`);
      autoAnswer(call.id);
    } catch (e) { toast.error("Erreur lors de l'appel"); }
  };

  const initiateVideoCall = async (contact) => {
    try {
      const call = await startOutgoing(contact, 'video-call', { callee_id: contact.id });
      toast.success(`Appel video vers ${contact.full_name}...`);
      autoAnswer(call.id);
    } catch (e) { toast.error("Erreur lors de l'appel video"); }
  };

  const initiatePhoneCall = async (contact) => {
    try {
      const call = await startOutgoing(contact, 'call-phone', { callee_id: contact.id, phone_number: contact.phone });
      if (call.mock) {
        toast.info(call.message);
        setTimeout(() => { setView('idle'); setCurrentCall(null); }, 3000);
      } else {
        toast.success(`Appel telephonique vers ${contact.phone}...`);
      }
    } catch (e) { toast.error("Erreur lors de l'appel telephonique"); }
  };

  const callAction = (action) => axios.post(`${API}/communications/call/${currentCall.id}/${action}`, {}, { headers });

  const answerCall = async () => {
    if (!currentCall) return;
    try {
      await callAction('answer');
      setView('active');
    } catch (e) { toast.error("Erreur pour repondre a l'appel"); }
  };

  const endCall = async () => {
    if (!currentCall) return;
    try { await callAction('end'); } catch (e) { void 0; }
    setView('idle');
    setCurrentCall(null);
    setSelectedContact(null);
    setDuration(0);
    setIsRecording(false);
    fetchRecentCalls();
  };

  const declineCall = async () => {
    if (!currentCall) return;
    try { await callAction('decline'); } catch (e) { void 0; }
    setView('idle');
    setCurrentCall(null);
  };

  const toggleRecording = async () => {
    if (!currentCall) return;
    try {
      const res = await callAction('record');
      setIsRecording(res.data.is_recording);
      toast.success(res.data.message);
    } catch (e) { toast.error('Erreur enregistrement'); }
  };

  return {
    user, isStaff, view, tab, setTab,
    isOpen, setIsOpen, isMinimized, setIsMinimized,
    contacts, search, setSearch, recentCalls,
    selectedContact, currentCall, duration, isRecording,
    isMuted, setIsMuted, isSpeakerOff, setIsSpeakerOff,
    initiateCall, initiatePhoneCall, initiateVideoCall,
    answerCall, endCall, declineCall, toggleRecording,
  };
}
